import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { PERMISSION_OBJECT_COPY, RESTRICTED_TRANSITION_LABELS, isLockedCell, type ModuleState } from '@nest-recruit/shared';
import { Drawer } from '../../../../components/Drawer';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { useRoleMatrix } from '../../api/roles-api';
import { compareText, visibleAreas, visibleTransitions } from './role-editor-model';
import { listedRoles, peopleText } from './RoleList';
import styles from './CompareRoles.module.css';

// "Compare all roles" (Roles B's table, inside A; DECISIONS-LOG 2026-11-02 Q4): every role side by
// side, read only, as a wide panel from the right. Its rows follow the editor's: hidden while their
// part is off, unless "Show them anyway" is on.

interface CompareRolesProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly modules: ModuleState;
  readonly showHidden: boolean;
}

export function CompareRoles({ isOpen, onClose, modules, showHidden }: CompareRolesProps): ReactNode {
  const matrix = useRoleMatrix(isOpen);
  const roles = listedRoles(matrix.data ?? []);
  const areas = visibleAreas(modules, showHidden);
  const transitions = visibleTransitions(modules, showHidden);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Compare all roles" meta="Read only. Pick a role on the page to change it." size="wide">
      {matrix.isPending ? (
        <SkeletonLines count={10} />
      ) : matrix.isError ? (
        <ServerErrorState requestId={matrix.error.requestId} onRetry={() => void matrix.refetch()} />
      ) : (
        <div className={styles.scroller}>
          <table className={styles.table}>
            <caption className={styles.caption}>What each role can open: “—” means hidden everywhere, a lock means fixed.</caption>
            <thead>
              <tr>
                <th scope="col" className={styles.corner}>
                  What
                </th>
                {roles.map((role) => (
                  <th key={role.id} scope="col" className={styles.roleHead}>
                    <span className={styles.roleName}>{role.label}</span>
                    <span className={styles.rolePeople}>{peopleText(role.assignedUserCount)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            {areas.map((area) => (
              <tbody key={area.key}>
                <tr>
                  <th scope="colgroup" colSpan={roles.length + 1} className={styles.area}>
                    {area.label}
                  </th>
                </tr>
                {area.objects.map((object) => (
                  <tr key={object}>
                    <th scope="row" className={styles.what}>
                      {PERMISSION_OBJECT_COPY[object].label}
                    </th>
                    {roles.map((role) => (
                      <td key={role.id} className={styles.cell}>
                        {isLockedCell(role.key, object) && <Lock size={12} aria-label="Fixed" />}
                        {compareText(role, object)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
            <tbody>
              <tr>
                <th scope="colgroup" colSpan={roles.length + 1} className={styles.area}>
                  Special actions
                </th>
              </tr>
              {transitions.map((key) => (
                <tr key={key}>
                  <th scope="row" className={styles.what}>
                    {RESTRICTED_TRANSITION_LABELS[key]}
                  </th>
                  {roles.map((role) => (
                    <td key={role.id} className={styles.cell}>
                      {role.transitions.find((transition) => transition.transitionKey === key)?.isAllowed ? 'Yes' : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
